import React, { useState, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight, faUser, faEnvelope, faPhone, faBoxOpen, faFileAlt,
    faLock, faCheckCircle, faHashtag, faSpinner, faShieldAlt,
    faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements, CardNumberElement, CardExpiryElement, CardCvcElement,
    useStripe, useElements,
} from '@stripe/react-stripe-js';
import { fetchPaymentByToken, confirmPayment } from '../services/paymentService';

const stripePromise = loadStripe('pk_live_51SAZtM0z7x1jbA3yUkdgRdrjkskiLmcVAsNMTDbPUWC3QojcbpjYmhvADoLPvdB6rl7LJFVUS0svguDsPWf9rjYm00LuAet1bx');

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#002536', fontFamily: '"Segoe UI", sans-serif',
            fontSize: '15px', fontSmoothing: 'antialiased',
            '::placeholder': { color: '#a0b4bf' },
        },
        invalid: { color: '#e74c3c', iconColor: '#e74c3c' },
    },
};

// ─── Inner checkout form (must live inside <Elements>) ────────────────────────
function CheckoutForm({ orderDetails, orderId, paymentToken }) {
    const stripe   = useStripe();
    const elements = useElements();
    const navigate = useNavigate();

    const [processing, setProcessing] = useState(false);
    const [cardError,  setCardError]  = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);
        setCardError('');

        try {
            const { error, paymentIntent } = await stripe.confirmCardPayment(
                window.__stripe_client_secret__,
                {
                    payment_method: {
                        card: elements.getElement(CardNumberElement),
                        billing_details: {
                            name:  orderDetails.customerName,
                            email: orderDetails.customerEmail,
                            phone: orderDetails.customerPhone,
                        },
                    },
                }
            );

            if (error) {
                setCardError(error.message);
                setProcessing(false);
                return;
            }

            if (paymentIntent.status === 'succeeded') {
                await confirmPayment({ paymentIntentId: paymentIntent.id, orderId });
                navigate('/payment-thankyou', {
                    state: {
                        orderNumber:     orderDetails.orderNumber,
                        customerName:    orderDetails.customerName,
                        customerEmail:   orderDetails.customerEmail,
                        packagePrice:    orderDetails.packagePrice,
                        serviceSelected: orderDetails.serviceSelected,
                    },
                });
            }
        } catch (err) {
            setCardError(err.message || 'Payment failed. Please try again.');
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="payment-form-section">
            <div className="payment-field">
                <label>
                    <span className="field-icon-wrap">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="1" y="4" width="22" height="16" rx="2"/>
                            <line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                    </span>
                    Card Number
                </label>
                <div className="stripe-element-wrap">
                    <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                </div>
            </div>

            <div className="payment-row">
                <div className="payment-field">
                    <label>Expiry Date</label>
                    <div className="stripe-element-wrap">
                        <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                </div>
                <div className="payment-field">
                    <label><FontAwesomeIcon icon={faLock} className="field-icon" /> CVV</label>
                    <div className="stripe-element-wrap">
                        <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                </div>
            </div>

            {cardError && (
                <div className="card-error-msg" role="alert">
                    <FontAwesomeIcon icon={faExclamationCircle} /> {cardError}
                </div>
            )}

            <div className="security-notice">
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>Your payment is secured with 256-bit SSL encryption</span>
            </div>

            <div className="stripe-badge">
                <span style={{ fontWeight: 800, color: '#635bff', fontSize: 15, letterSpacing: -0.5 }}>stripe</span>
                <span style={{ fontSize: 12, color: '#8a99ad', marginLeft: 4 }}>Powered by Stripe</span>
            </div>

            <button type="submit" className="complete-payment-btn" disabled={!stripe || processing}>
                {processing
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing payment...</>
                    : <><FontAwesomeIcon icon={faCheckCircle} /> Pay ${Number(orderDetails.packagePrice).toFixed(2)}</>
                }
            </button>
        </form>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CompletePayment() {
    const { token }  = useParams();  // /complete-payment/:token
    const navigate   = useNavigate();

    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState('');
    const [alreadyPaid,  setAlreadyPaid]  = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    const [orderId,      setOrderId]      = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);

    useEffect(() => {
        if (!token) { navigate('/', { replace: true }); return; }

        fetchPaymentByToken(token)
            .then(data => {
                window.__stripe_client_secret__ = data.clientSecret;
                setClientSecret(data.clientSecret);
                setOrderId(data.orderId);
                setOrderDetails(data.orderDetails);
            })
            .catch(err => {
                if (err.alreadyPaid) setAlreadyPaid(true);
                else setError(err.message || 'This payment link is invalid or has expired.');
            })
            .finally(() => setLoading(false));
    }, [token]);

    // ── Loading state ──
    if (loading) {
        return (
            <>
                <Header />
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 36, color: '#1d6a88', marginBottom: 16 }} />
                        <p style={{ color: '#5a7a8a', fontSize: 15 }}>Loading payment details...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // ── Already paid ──
    if (alreadyPaid) {
        return (
            <>
                <Header />
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
                        <h2 style={{ color: '#002536', fontWeight: 800, marginBottom: 12 }}>Payment Already Completed</h2>
                        <p style={{ color: '#5a7a8a', fontSize: 15, lineHeight: 1.7 }}>
                            This payment has already been processed successfully. Thank you!
                        </p>
                        <Link to="/" style={{ display: 'inline-block', marginTop: 24, background: 'linear-gradient(135deg,#00a8cc,#1d6a88)', color: '#fff', padding: '13px 32px', borderRadius: 50, textDecoration: 'none', fontWeight: 700 }}>
                            Back to Home
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // ── Invalid link ──
    if (error) {
        return (
            <>
                <Header />
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
                    <div style={{ textAlign: 'center', maxWidth: 480 }}>
                        <div style={{ fontSize: 56, marginBottom: 20 }}>🔗</div>
                        <h2 style={{ color: '#002536', fontWeight: 800, marginBottom: 12 }}>Invalid Payment Link</h2>
                        <p style={{ color: '#5a7a8a', fontSize: 15, lineHeight: 1.7 }}>{error}</p>
                        <Link to="/" style={{ display: 'inline-block', marginTop: 24, background: 'linear-gradient(135deg,#00a8cc,#1d6a88)', color: '#fff', padding: '13px 32px', borderRadius: 50, textDecoration: 'none', fontWeight: 700 }}>
                            Back to Home
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const stripeOptions = {
        clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#00a8cc' } },
    };

    return (
        <>
            <Header />

            <section className="breadcrumbsection">
                <div className='container'>
                    <h1>Complete Payment</h1>
                    <ul className='breadcrumblinks'>
                        <li><Link to="/">Home</Link></li>
                        <li><FontAwesomeIcon icon={faChevronRight} /></li>
                        <li>Complete Payment</li>
                    </ul>
                </div>
            </section>

            <section className="complete-payment-main-section">
                <div className="container">
                    <div className="complete-payment-wrapper">
                        <div className="row g-4">

                            {/* ── Order Summary ── */}
                            <div className="col-lg-7">
                                <div className="order-summary-card">
                                    <div className="card-header">
                                        <h3>Order Summary</h3>
                                        <p>Review your order details</p>
                                    </div>
                                    <div className="order-details">
                                        <div className="detail-section">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="detail-field">
                                                        <label><FontAwesomeIcon icon={faHashtag} className="field-icon" /> Order Number</label>
                                                        <input type="text" value={orderDetails.orderNumber} className="readonly-field" readOnly />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="detail-field">
                                                        <label><FontAwesomeIcon icon={faUser} className="field-icon" /> Customer Name</label>
                                                        <input type="text" value={orderDetails.customerName} className="readonly-field" readOnly />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mt-3">
                                                    <div className="detail-field">
                                                        <label><FontAwesomeIcon icon={faEnvelope} className="field-icon" /> Email Address</label>
                                                        <input type="text" value={orderDetails.customerEmail} className="readonly-field" readOnly />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mt-3">
                                                    <div className="detail-field">
                                                        <label><FontAwesomeIcon icon={faPhone} className="field-icon" /> Phone Number</label>
                                                        <input type="text" value={orderDetails.customerPhone || '—'} className="readonly-field" readOnly />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="detail-section">
                                            <div className="detail-field">
                                                <label><FontAwesomeIcon icon={faBoxOpen} className="field-icon" /> Service Selected</label>
                                                <input type="text" value={orderDetails.serviceSelected} className="readonly-field" readOnly />
                                            </div>
                                            {orderDetails.packageDetails && (
                                                <div className="detail-field">
                                                    <label><FontAwesomeIcon icon={faFileAlt} className="field-icon" /> Package Details</label>
                                                    <textarea value={orderDetails.packageDetails} className="readonly-field" rows="3" readOnly />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Payment Form ── */}
                            <div className="col-lg-5">
                                <div className="payment-card">
                                    <div className="card-header">
                                        <h3>Payment Details</h3>
                                        <p>Enter your card information</p>
                                    </div>
                                    <Elements stripe={stripePromise} options={stripeOptions}>
                                        <CheckoutForm
                                            orderDetails={orderDetails}
                                            orderId={orderId}
                                            paymentToken={token}
                                        />
                                    </Elements>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
            <Footer />
        </>
    );
}